import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import {
  SITE_URL,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  LOCALE_MAP,
  getPathWithoutLang,
} from '../../constants/languages';

const OG_IMAGE = `${SITE_URL}/og-image.png`;

function removePreRenderSeoTags() {
  document.querySelectorAll('[data-seo-pre]').forEach(el => el.remove());
}

const SEO = ({ titleKey, descriptionKey, keywords, jsonLd, noindex }) => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const location = useLocation();
  const cleaned = useRef(false);

  useEffect(() => {
    if (!cleaned.current) {
      removePreRenderSeoTags();
      cleaned.current = true;
    }
  }, []);

  const currentLang = lang || i18n.language || DEFAULT_LANGUAGE;
  const title = titleKey ? t(titleKey) : 'PetekB2B - B2B E-Commerce Platform';
  const description = descriptionKey ? t(descriptionKey) : t('footer.description');

  const pathWithoutLang = getPathWithoutLang(location.pathname);
  const canonicalUrl = `${SITE_URL}/${currentLang}${pathWithoutLang}`;

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'PetekB2B',
    legalName: 'Boranka Teknoloji A.Ş.',
    url: SITE_URL,
    logo: `${SITE_URL}/logo192.png`,
    sameAs: [
      'https://www.linkedin.com/products/boranka-petekb2b/',
      'https://www.youtube.com/@petekb2b',
      'https://www.instagram.com/borankatek/'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@petekb2b.com',
      contactType: 'sales'
    }
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'PetekB2B',
    url: SITE_URL,
    inLanguage: SUPPORTED_LANGUAGES
  };

  return (
    <Helmet>
      <html lang={currentLang} dir={currentLang === 'ar' ? 'rtl' : 'ltr'} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="PetekB2B" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={LOCALE_MAP[currentLang] || 'en_US'} />
      {SUPPORTED_LANGUAGES.filter(l => l !== currentLang).map(l => (
        <meta key={l} property="og:locale:alternate" content={LOCALE_MAP[l]} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {/* Self-referencing canonical: each language version points to itself */}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang: all language versions including self-reference (Google requirement) */}
      {SUPPORTED_LANGUAGES.map(l => (
        <link key={l} rel="alternate" hrefLang={l} href={`${SITE_URL}/${l}${pathWithoutLang}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/${DEFAULT_LANGUAGE}${pathWithoutLang}`} />

      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export default SEO;
